import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

async function recordAccept(checkAuth) {
  await api.post("/me/accept-terms");
  await checkAuth();
}

// Global modal shown once per session after login when the user hasn't accepted the current Terms version.
export default function TermsReconsentModal() {
  const { user, checkAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.terms_current === false && !sessionStorage.getItem("sam_reconsent_dismissed")) {
      setOpen(true);
    }
  }, [user]);

  if (!user || user.terms_current !== false) return null;

  const agree = async () => {
    setBusy(true);
    try { await recordAccept(checkAuth); toast.success("Thanks — your acceptance is recorded."); setOpen(false); }
    catch { toast.error("Could not record acceptance. Please try again."); }
    finally { setBusy(false); }
  };
  const later = () => { sessionStorage.setItem("sam_reconsent_dismissed", "1"); setOpen(false); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) later(); }}>
      <DialogContent data-testid="reconsent-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-sam-green" />We've updated our Terms</DialogTitle>
          <DialogDescription>We've published a new version of our Terms of Service. Please review and accept to keep using Saving America Money.</DialogDescription>
        </DialogHeader>
        <Link to="/terms" target="_blank" rel="noreferrer" className="text-sam-crimson font-semibold underline text-sm" data-testid="reconsent-review-link">Read the full Terms of Service →</Link>
        <div className="flex gap-2 mt-2">
          <Button variant="outline" className="flex-1" onClick={later} data-testid="reconsent-later-btn">Remind me later</Button>
          <Button className="flex-1 bg-sam-crimson hover:bg-sam-crimson/90" onClick={agree} disabled={busy} data-testid="reconsent-agree-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}I agree</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Persistent dashboard banner reminding the user to accept the updated Terms.
export function TermsReconsentBanner() {
  const { user, checkAuth } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.terms_current !== false) return null;
  const agree = async () => {
    setBusy(true);
    try { await recordAccept(checkAuth); toast.success("Thanks — your acceptance is recorded."); }
    catch { toast.error("Could not record acceptance. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-6 rounded-xl border border-sam-amber/50 bg-sam-amber/10 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="reconsent-banner">
      <div className="text-sm text-sam-navy"><b>Our Terms of Service were updated.</b> Please review and accept to keep your account in good standing.</div>
      <div className="flex gap-2">
        <Button asChild variant="outline" size="sm"><Link to="/terms" target="_blank" rel="noreferrer" data-testid="reconsent-banner-review">Review</Link></Button>
        <Button size="sm" className="bg-sam-crimson hover:bg-sam-crimson/90" onClick={agree} disabled={busy} data-testid="reconsent-banner-accept">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Accept</Button>
      </div>
    </div>
  );
}
